/**
 * Live presence + filter sync for Project Hub Gantt timelines.
 *
 * `ganttTimelineRooms/{roomId}/presence/{uid}` — who is viewing, cursor, viewHash, syncId
 * `ganttTimelineRooms/{roomId}/sync/{syncId}` — shared outline for synced peers
 */
import { db } from './firebase';
import {
  arrayUnion,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  BOARD_PRESENCE_STALE_MS,
  cursorColorForUid,
} from './boardPresenceService';
import {
  ganttTimelineRoomId,
  parseGanttSyncedViewport,
  type GanttSyncedOutline,
  type GanttSyncedViewport,
} from './ganttTimelineViewSync';

export { ganttTimelineRoomId };

export {
  cursorColorForUid,
  BOARD_PRESENCE_STALE_MS as GANTT_TIMELINE_PRESENCE_STALE_MS,
};

export const GANTT_TIMELINE_CURSOR_THROTTLE_MS = 150;
export const GANTT_TIMELINE_OUTLINE_SYNC_THROTTLE_MS = 300;
export const GANTT_TIMELINE_VIEWPORT_THROTTLE_MS = 100;

export type GanttTimelineCursorPane = 'chart' | 'left';

export interface GanttTimelinePeerPresence {
  uid: string;
  name: string;
  color: string;
  viewHash: string;
  syncId: string | null;
  rowKey: string | null;
  timeMs: number | null;
  pane: GanttTimelineCursorPane;
  outline: GanttSyncedOutline | null;
  viewport: GanttSyncedViewport | null;
  updatedAt: Date | null;
}

function presenceDoc(roomId: string, uid: string) {
  return doc(db, 'ganttTimelineRooms', roomId, 'presence', uid);
}

function syncDoc(roomId: string, syncId: string) {
  return doc(db, 'ganttTimelineRooms', roomId, 'sync', syncId);
}

export function isFreshGanttTimelinePeer(
  p: GanttTimelinePeerPresence,
  now = Date.now(),
): boolean {
  return !!p.updatedAt && now - p.updatedAt.getTime() < BOARD_PRESENCE_STALE_MS;
}

function parseOutline(raw: unknown): GanttSyncedOutline | null {
  if (!raw || typeof raw !== 'object') return null;
  return raw as GanttSyncedOutline;
}

export function subscribeGanttTimelinePresence(
  roomId: string,
  selfUid: string,
  callback: (peers: GanttTimelinePeerPresence[]) => void,
): () => void {
  return onSnapshot(
    collection(db, 'ganttTimelineRooms', roomId, 'presence'),
    (snap) => {
      const peers = snap.docs
        .map((d) => {
          const v = d.data();
          const updatedAtRaw = v.updatedAt as { toDate?: () => Date } | undefined;
          const pane: GanttTimelineCursorPane =
            v.pane === 'left' ? 'left' : 'chart';
          return {
            uid: d.id,
            name: String(v.name || 'Someone'),
            color: String(v.color || '#6366f1'),
            viewHash: String(v.viewHash || ''),
            syncId: v.syncId ? String(v.syncId) : null,
            rowKey: v.rowKey ? String(v.rowKey) : null,
            timeMs: typeof v.timeMs === 'number' ? v.timeMs : null,
            pane,
            outline: parseOutline(v.outline),
            viewport: parseGanttSyncedViewport(v.viewport),
            updatedAt: updatedAtRaw?.toDate?.() ?? null,
          };
        })
        .filter((p) => p.uid !== selfUid);
      callback(peers);
    },
    (err) => {
      console.warn('[ganttTimelinePresenceService] subscribe failed', err);
      callback([]);
    },
  );
}

export async function publishGanttTimelinePresence(
  roomId: string,
  payload: {
    uid: string;
    name: string;
    color: string;
    viewHash: string;
    syncId?: string | null;
    outline?: GanttSyncedOutline | null;
    viewport?: GanttSyncedViewport | null;
    rowKey?: string | null;
    timeMs?: number | null;
    pane?: GanttTimelineCursorPane;
  },
): Promise<void> {
  try {
    const data: Record<string, unknown> = {
      name: payload.name,
      color: payload.color,
      viewHash: payload.viewHash,
      syncId: payload.syncId ?? null,
      updatedAt: serverTimestamp(),
    };
    if (payload.outline !== undefined) {
      data.outline = payload.outline;
    }
    if (payload.viewport !== undefined) {
      data.viewport = payload.viewport;
    }
    if (payload.rowKey !== undefined) {
      data.rowKey = payload.rowKey;
    }
    if (payload.timeMs !== undefined) {
      data.timeMs = payload.timeMs;
    }
    if (payload.pane !== undefined) {
      data.pane = payload.pane;
    }
    await setDoc(presenceDoc(roomId, payload.uid), data, { merge: true });
  } catch (err) {
    console.warn('[ganttTimelinePresenceService] publish failed', err);
  }
}

export async function clearGanttTimelinePresence(roomId: string, uid: string): Promise<void> {
  try {
    await deleteDoc(presenceDoc(roomId, uid));
  } catch {
    /* stale filter handles missed deletes */
  }
}

export interface GanttTimelineSyncDoc {
  syncId: string;
  outline: GanttSyncedOutline;
  viewport: GanttSyncedViewport | null;
  memberUids: string[];
  updatedBy: string;
  updatedAt: Date | null;
}

export function subscribeGanttTimelineSync(
  roomId: string,
  syncId: string,
  callback: (doc: GanttTimelineSyncDoc | null) => void,
): () => void {
  return onSnapshot(
    syncDoc(roomId, syncId),
    (snap) => {
      if (!snap.exists()) {
        callback(null);
        return;
      }
      const v = snap.data();
      const updatedAtRaw = v.updatedAt as { toDate?: () => Date } | undefined;
      const members = Array.isArray(v.memberUids)
        ? v.memberUids.filter((u): u is string => typeof u === 'string')
        : [];
      callback({
        syncId: snap.id,
        outline: parseOutline(v.outline) ?? {
          version: 1,
          mode: 'single',
          viewHash: '',
          structureFilter: null,
          gantt: {
            collapsedGroups: [],
            collapsedSections: [],
            expandedParentIds: [],
            pinKeys: [],
          },
        },
        viewport: parseGanttSyncedViewport(v.viewport),
        memberUids: members,
        updatedBy: String(v.updatedBy || ''),
        updatedAt: updatedAtRaw?.toDate?.() ?? null,
      });
    },
    (err) => {
      console.warn('[ganttTimelinePresenceService] sync subscribe failed', err);
      callback(null);
    },
  );
}

export async function joinGanttTimelineSync(params: {
  roomId: string;
  syncId: string;
  uid: string;
  outline: GanttSyncedOutline;
}): Promise<void> {
  const { roomId, syncId, uid, outline } = params;
  try {
    await setDoc(
      syncDoc(roomId, syncId),
      {
        outline,
        memberUids: arrayUnion(uid),
        updatedBy: uid,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
    await setDoc(
      presenceDoc(roomId, uid),
      { syncId, outline, updatedAt: serverTimestamp() },
      { merge: true },
    );
  } catch (err) {
    console.warn('[ganttTimelinePresenceService] join sync failed', err);
  }
}

export async function publishGanttTimelineSyncViewport(params: {
  roomId: string;
  syncId: string;
  uid: string;
  viewport: GanttSyncedViewport;
}): Promise<void> {
  const { roomId, syncId, uid, viewport } = params;
  try {
    await setDoc(
      syncDoc(roomId, syncId),
      {
        viewport,
        updatedBy: uid,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
    await setDoc(
      presenceDoc(roomId, uid),
      { viewport, updatedAt: serverTimestamp() },
      { merge: true },
    );
  } catch (err) {
    console.warn('[ganttTimelinePresenceService] publish sync viewport failed', err);
  }
}

export async function publishGanttTimelineSyncOutline(params: {
  roomId: string;
  syncId: string;
  uid: string;
  outline: GanttSyncedOutline;
}): Promise<void> {
  const { roomId, syncId, uid, outline } = params;
  try {
    await setDoc(
      syncDoc(roomId, syncId),
      {
        outline,
        updatedBy: uid,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
    await setDoc(
      presenceDoc(roomId, uid),
      { outline, viewHash: outline.viewHash ?? '', updatedAt: serverTimestamp() },
      { merge: true },
    );
  } catch (err) {
    console.warn('[ganttTimelinePresenceService] publish sync outline failed', err);
  }
}

export async function leaveGanttTimelineSync(params: {
  roomId: string;
  syncId: string;
  uid: string;
}): Promise<void> {
  const { roomId, syncId, uid } = params;
  try {
    await updateDoc(presenceDoc(roomId, uid), {
      syncId: deleteField(),
    });
    const ref = syncDoc(roomId, syncId);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const v = snap.data();
      const members = Array.isArray(v.memberUids)
        ? v.memberUids.filter((u: unknown) => typeof u === 'string' && u !== uid)
        : [];
      if (members.length === 0) {
        await deleteDoc(ref);
      } else {
        await updateDoc(ref, { memberUids: members });
      }
    }
  } catch (err) {
    console.warn('[ganttTimelinePresenceService] leave sync failed', err);
  }
}
